import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { AdminPageHeader } from '../../layouts/admin-page-header.js';
import { AdminContent } from '../../layouts/admin-content.js';
import { AdminSectionCard } from '../../components/primitives/admin-section-card.js';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminStatusBadge, AdminOrderStatusBadge } from '../../components/primitives/admin-status-badge.js';
import { AdminPermissionGate } from '../../components/primitives/admin-permission-gate.js';
import { AdminApiError } from '../../api/admin-client.js';
import { formatPaise, formatDate, formatDateTime } from '../../lib/format.js';
import { useAdminCustomerProfile, useAdminCustomerStatusMutation } from '../hooks/use-customers.js';
import { AccountStatusDialog } from '../components/account-status-dialog.js';

const TABS = ['Overview', 'Orders', 'Addresses', 'Rewards & Coupons', 'Payments & Refunds', 'Notifications', 'Account activity'] as const;
type Tab = (typeof TABS)[number];

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  locked: 'warning',
  suspended: 'danger',
  disabled: 'neutral',
  deleted: 'neutral',
};

export function CustomerProfilePage() {
  const { customerId } = useParams<{ customerId: string }>();
  const { data, isLoading, isError, refetch } = useAdminCustomerProfile(customerId);
  const statusMutation = useAdminCustomerStatusMutation();
  const [tab, setTab] = useState<Tab>('Overview');
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <AdminContent>
      <AdminPageHeader
        title={data?.fullName ?? data?.email ?? 'Customer'}
        breadcrumbs={[{ label: 'Customers', to: ROUTE_NAMES.adminCustomers }, { label: data?.email ?? '' }]}
        actions={
          data && (
            <AdminPermissionGate anyRole={['ADMIN']}>
              <button
                type="button"
                onClick={() => setShowStatusDialog(true)}
                disabled={data.allowedActions.length === 0}
                className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Account action
              </button>
            </AdminPermissionGate>
          )
        }
      />

      {actionError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      {isLoading && <AdminLoadingState label="Loading customer…" />}
      {isError && <AdminErrorState message="This customer could not be loaded." onRetry={() => void refetch()} />}

      {data && (
        <>
          <AdminSectionCard title="Overview" className="mb-4">
            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-gray-500">Status</dt>
                <dd>
                  <AdminStatusBadge label={data.status} tone={STATUS_TONE[data.status] ?? 'neutral'} />
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd>{data.email}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Phone</dt>
                <dd>{data.phone ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Joined</dt>
                <dd>{formatDate(data.joinedAt)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Orders</dt>
                <dd>{data.orderStats.orderCount}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Total spend</dt>
                <dd>{formatPaise(data.orderStats.totalSpend.amountPaise)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last order</dt>
                <dd>{data.orderStats.lastOrderAt ? formatDate(data.orderStats.lastOrderAt) : '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Last seen</dt>
                <dd>{data.lastSeenAt ? formatDateTime(data.lastSeenAt) : '—'}</dd>
              </div>
            </dl>
          </AdminSectionCard>

          <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-100" role="tablist">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={cn(
                  'rounded-t-lg px-3 py-2 text-sm font-medium',
                  tab === t ? 'border-b-2 border-gray-900 text-gray-900' : 'text-gray-500 hover:text-gray-700',
                )}
              >
                {t}
              </button>
            ))}
          </div>

          {tab === 'Orders' && (
            <AdminSectionCard title="Recent orders">
              {data.recentOrders.length === 0 ? (
                <p className="text-sm text-gray-500">No orders yet.</p>
              ) : (
                <ul className="space-y-2">
                  {data.recentOrders.map((order) => (
                    <li key={order.orderNumber} className="flex items-center justify-between text-sm">
                      <Link to={`${ROUTE_NAMES.adminOrders}/${order.orderNumber}`} className="hover:underline">
                        {order.orderNumber}
                      </Link>
                      <span className="flex items-center gap-2">
                        <AdminOrderStatusBadge status={order.status} />
                        {formatPaise(order.total.amountPaise)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </AdminSectionCard>
          )}

          {tab === 'Addresses' && (
            <AdminSectionCard title="Addresses">
              {data.addresses.length === 0 ? (
                <p className="text-sm text-gray-500">No saved addresses.</p>
              ) : (
                <ul className="space-y-3">
                  {data.addresses.map((address, index) => (
                    <li key={index} className="text-sm">
                      <p className="font-medium text-gray-900">
                        {address.recipientName} {address.isDefault && <span className="text-xs text-gray-400">(default)</span>}
                      </p>
                      <p className="text-gray-500">
                        {address.city}, {address.state} {address.pincode}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </AdminSectionCard>
          )}

          {tab === 'Rewards & Coupons' && (
            <div className="space-y-4">
              <AdminSectionCard title="Coupon redemptions">
                {data.coupons.length === 0 ? (
                  <p className="text-sm text-gray-500">No coupons redeemed.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.coupons.map((coupon, index) => (
                      <li key={index}>
                        {coupon.code} — {formatPaise(coupon.discountApplied.amountPaise)} off order {coupon.orderNumber}
                      </li>
                    ))}
                  </ul>
                )}
              </AdminSectionCard>
              <AdminSectionCard title="Reward spin history">
                {data.spinHistory.length === 0 ? (
                  <p className="text-sm text-gray-500">No reward spins yet.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.spinHistory.map((spin, index) => (
                      <li key={index}>{formatDateTime(spin.spunAt)}</li>
                    ))}
                  </ul>
                )}
              </AdminSectionCard>
              <AdminSectionCard title="Reward coupons">
                {data.rewardCoupons.length === 0 ? (
                  <p className="text-sm text-gray-500">No reward coupons issued.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.rewardCoupons.map((coupon, index) => (
                      <li key={index}>
                        {coupon.code} — {coupon.isActive ? 'active' : 'inactive'}, expires{' '}
                        {formatDate(coupon.expiresAt)}
                      </li>
                    ))}
                  </ul>
                )}
              </AdminSectionCard>
            </div>
          )}

          {tab === 'Payments & Refunds' && (
            <AdminSectionCard title="Payments & refunds">
              <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-gray-500">Payments</dt>
                  <dd>{data.paymentSummary.paymentCount}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Total captured</dt>
                  <dd>{formatPaise(data.paymentSummary.totalCaptured.amountPaise)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Refunds</dt>
                  <dd>{data.refundSummary.refundCount}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Total refunded</dt>
                  <dd>{formatPaise(data.refundSummary.totalRefunded.amountPaise)}</dd>
                </div>
              </dl>
            </AdminSectionCard>
          )}

          {tab === 'Notifications' && (
            <AdminSectionCard title="Notifications">
              {data.notifications.length === 0 ? (
                <p className="text-sm text-gray-500">No notifications yet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.notifications.map((notification, index) => (
                    <li key={index} className="flex items-center justify-between">
                      <span>{notification.title}</span>
                      <span className="text-xs text-gray-400">{formatDateTime(notification.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </AdminSectionCard>
          )}

          {tab === 'Account activity' && (
            <AdminSectionCard title="Login activity">
              {data.loginActivity.length === 0 ? (
                <p className="text-sm text-gray-500">No login activity recorded.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.loginActivity.map((event, index) => (
                    <li key={index}>
                      {event.eventType.replace(/_/g, ' ')} · {formatDateTime(event.createdAt)}
                    </li>
                  ))}
                </ul>
              )}
            </AdminSectionCard>
          )}
        </>
      )}

      {showStatusDialog && data && (
        <AccountStatusDialog
          customerEmail={data.email}
          allowedActions={data.allowedActions}
          isSubmitting={statusMutation.isPending}
          onClose={() => setShowStatusDialog(false)}
          onSubmit={(input) => {
            setActionError(null);
            statusMutation.mutate(
              { customerId: data.id, input },
              {
                onSuccess: () => setShowStatusDialog(false),
                onError: (error) =>
                  setActionError(error instanceof AdminApiError ? error.message : 'Could not update account status.'),
              },
            );
          }}
        />
      )}
    </AdminContent>
  );
}
